/**
 * Product configuration for shared frontend code.
 *
 * Every JuristAI frontend is one build of the same dashboard parameterised by
 * an appId (1 Atticus, 2 LitigAI, 3/4 SeriesAI, 5 in-house) plus an API base
 * URL. Until now each fork hard-coded its own default inline
 * (`process.env.REACT_APP_APP_ID || "2"`, `|| "1"`, ...), which is the single
 * biggest reason a lib module from one fork cannot be shared with another.
 *
 * Shared code must never embed a product default. It calls `getAppId()` /
 * `resolveApiBaseUrl()` here, and the HOST app declares its identity once at
 * bootstrap with `configureProduct(...)`. Hosts that resolve the appId at
 * runtime (SeriesAI serves appIds 3 and 4 from one build, keyed on the route)
 * pass their own `resolveAppId` function and keep that logic.
 *
 * Resolution order for `getAppId(...candidates)`:
 *   1. the host's `resolveAppId(candidates)` if configured and it returns a value
 *   2. the first explicit candidate that normalises to a non-empty string
 *      (and, when the host declared `appIds`, is one of them)
 *   3. `process.env.REACT_APP_APP_ID` (inlined by CRA at build time)
 *   4. the host's configured `appId`
 *   5. `undefined` (never a silent product default)
 *
 * Always returns a string: GraphQL variables typed String reject numbers.
 */

const DEFAULT_STATE = Object.freeze({
  appId: undefined,
  appIds: undefined,
  resolveAppId: undefined,
  apiBaseUrl: undefined,
  // Authenticated HTTP client (an axios instance, or a thunk returning one).
  // Each app keeps its own axiosInstance with its interceptors, token refresh
  // and routing; shared API modules only ever ask for "the client".
  apiClient: undefined,
  // Optional host override for the /api/core origin (string or thunk).
  coreApiBase: undefined,
  // Optional host mapping from an app-relative route ("/dashboard/x") to the
  // path this build actually serves it at (SeriesAI/in-house prefix routes
  // with the dashboard appId). Identity when absent.
  routePath: undefined,
  // The host's Redux store (or a thunk returning it / its module). Shared API
  // modules only ever read the access token from it.
  store: undefined,
  // The host's toast module ({ error, success }) or a thunk returning it, so
  // shared API wrappers can surface failures without importing a UI library.
  toast: undefined,
  // Which env vars name the API origin, in precedence order, when the host
  // supplies no apiBaseUrl thunk. Forks disagree: SeriesAI/in-house never read
  // REACT_APP_API_URL (their .env sets it to a local dev server), Atticus reads
  // REACT_APP_ATTICUS_API_URL first. Default is LitigAI's order.
  apiEnvKeys: Object.freeze(["REACT_APP_API_URL", "REACT_APP_API_BASE_URL", "REACT_APP_ATTICUS_API_URL", "REACT_APP_JURIST_API_URL"]),
  features: Object.freeze({}),
  labels: Object.freeze({}),
});

// The descriptor lives on globalThis, not in module scope: host tests call
// jest.resetModules() and then require a shared module, which would otherwise
// get a fresh, unconfigured copy of this file while setupTests had configured
// the previous one. resetProduct() still clears it.
const GLOBAL_KEY = "__juristaiProductState__";
const globalHolder = typeof globalThis !== "undefined" ? globalThis : {};
globalHolder[GLOBAL_KEY] = globalHolder[GLOBAL_KEY] || { ...DEFAULT_STATE };
// The package can be loaded through both its root entry and a `/src/...`
// subpath. Keep every copy reading the same live descriptor: a module-local
// snapshot goes stale when another copy calls configureProduct().
const state = new Proxy({}, {
  get(_target, key) {
    return (globalHolder[GLOBAL_KEY] || DEFAULT_STATE)[key];
  },
  ownKeys() {
    return Reflect.ownKeys(globalHolder[GLOBAL_KEY] || DEFAULT_STATE);
  },
  getOwnPropertyDescriptor(_target, key) {
    const descriptor = Object.getOwnPropertyDescriptor(
      globalHolder[GLOBAL_KEY] || DEFAULT_STATE,
      key,
    );
    return descriptor ? { ...descriptor, configurable: true } : undefined;
  },
});

export function normalizeAppId(value) {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (!text) return "";
  // "4", 4, "4.0" and " 4 " all mean appId 4; anything non-numeric is kept
  // verbatim so hosts with symbolic ids are not broken.
  const numeric = Number(text);
  if (Number.isFinite(numeric) && /^\d+(\.0+)?$/.test(text)) return String(Math.trunc(numeric));
  return text;
}

/**
 * Declare the host product. Call once, before any shared module is used.
 * Unknown keys are kept so hosts can extend the descriptor.
 */
export function configureProduct(descriptor = {}) {
  const next = { ...state, ...descriptor };
  if (descriptor.appId !== undefined) next.appId = normalizeAppId(descriptor.appId) || undefined;
  if (descriptor.appIds !== undefined) {
    next.appIds = Array.isArray(descriptor.appIds)
      ? descriptor.appIds.map(normalizeAppId).filter(Boolean)
      : undefined;
  }
  if (descriptor.features !== undefined) next.features = Object.freeze({ ...(descriptor.features || {}) });
  if (descriptor.labels !== undefined) next.labels = Object.freeze({ ...(descriptor.labels || {}) });
  globalHolder[GLOBAL_KEY] = next;
  return getProduct();
}

/** Test helper: forget everything a host configured. */
export function resetProduct() {
  globalHolder[GLOBAL_KEY] = { ...DEFAULT_STATE };
}

export function getProduct() {
  return { ...state };
}

function readEnvAppId() {
  try {
    return normalizeAppId(typeof process !== "undefined" ? process.env?.REACT_APP_APP_ID : undefined);
  } catch (error) {
    return "";
  }
}

function isAllowed(appId) {
  if (!appId) return false;
  if (!state.appIds || state.appIds.length === 0) return true;
  return state.appIds.includes(appId);
}

export function getAppId(...candidates) {
  if (typeof state.resolveAppId === "function") {
    const resolved = normalizeAppId(state.resolveAppId(...candidates));
    if (resolved) return resolved;
  }
  for (const candidate of candidates) {
    const normalized = normalizeAppId(candidate);
    if (isAllowed(normalized)) return normalized;
  }
  const fromEnv = readEnvAppId();
  if (isAllowed(fromEnv)) return fromEnv;
  return state.appId;
}

export function isAppId(value, appId) {
  const normalized = normalizeAppId(value);
  if (!normalized) return false;
  return normalized === normalizeAppId(appId === undefined ? getAppId() : appId);
}

/**
 * True when `value` is an appId this build is allowed to act for. Hosts that
 * declare `appIds` (one SeriesAI build serves 3 and 4; in-house serves 5) get a
 * hard guard; hosts that declare none (LitigAI, Atticus) accept any appId.
 */
export function isDeclaredAppId(value) {
  return isAllowed(normalizeAppId(value));
}

/** True when the host declared this feature on; undefined features are off. */
export function hasFeature(name) {
  return Boolean(state.features && state.features[name]);
}

export function getLabel(name, fallback = "") {
  const value = state.labels ? state.labels[name] : undefined;
  return value === undefined || value === null ? fallback : value;
}

function trimTrailingSlash(url) {
  return typeof url === "string" ? url.trim().replace(/\/+$/, "") : "";
}

/**
 * REST API origin for axios clients. Host configuration wins; otherwise the
 * env precedence LitigAI's axiosInstance uses (REACT_APP_API_URL, then
 * REACT_APP_API_BASE_URL, then REACT_APP_ATTICUS_API_URL), with juristai-home's
 * REACT_APP_JURIST_API_URL as the final fallback. Returns "" (same-origin)
 * when nothing is configured.
 */
export function resolveApiBaseUrl() {
  // Hosts pass their own resolver as a thunk (LitigAI/SeriesAI/in-house:
  // axiosInstance.resolveApiBaseUrl; Atticus: apiBase.resolveApiBaseUrl) so
  // each app's localhost/origin rules keep applying to shared API modules.
  const configured = trimTrailingSlash(unwrapThunk(state.apiBaseUrl));
  if (configured) return configured;
  const env = typeof process !== "undefined" ? process.env || {} : {};
  for (const key of state.apiEnvKeys || DEFAULT_STATE.apiEnvKeys) {
    const value = trimTrailingSlash(env[key]);
    if (value) return value;
  }
  return "";
}

/**
 * The API base named by this host's environment configuration only (its
 * `apiEnvKeys`, in order), ignoring the host resolver. Differs from
 * resolveApiBaseUrl() in local development, where a host resolver returns the
 * page origin because requests go through the dev proxy.
 */
export function resolveConfiguredApiEnvBaseUrl() {
  const env = typeof process !== "undefined" ? process.env || {} : {};
  for (const key of state.apiEnvKeys || DEFAULT_STATE.apiEnvKeys) {
    const value = trimTrailingSlash(env[key]);
    if (value) return value;
  }
  return "";
}

function unwrapThunk(value) {
  return typeof value === "function" ? value() : value;
}

/**
 * The host's authenticated HTTP client. Shared API modules call this at
 * request time (never at module load) so the host can configure it lazily
 * and tests can swap it per case. Throws when no host configured one, which
 * is always a wiring error, never a runtime condition to swallow.
 */
export function getApiClient() {
  const configuredClient = state.apiClient;
  // Axios instances are callable functions as well as objects with request
  // methods. Preserve the instance instead of invoking it as a lazy thunk.
  let client =
    typeof configuredClient === "function" &&
    (typeof configuredClient.request === "function" ||
      typeof configuredClient.post === "function")
      ? configuredClient
      : unwrapThunk(configuredClient);
  // Host thunks `require()` their axiosInstance module. Babel interop gives
  // a real ESM module `{ default }`; a Jest manual mock may be a bare object
  // `{ post }` or `{ __esModule, default }`. Accept all three shapes.
  if (client && typeof client === "object" && !client.request && !client.post && client.default) {
    client = client.default;
  }
  if (!client) {
    throw new Error(
      "@juristai/shared: no apiClient configured. Call configureProduct({ apiClient }) in the host's productConfig.js.",
    );
  }
  return client;
}

const DEFAULT_REMOTE_CORE_API_BASE = "https://api-dev.juristai.org/api/core";

function windowOrigin() {
  if (typeof window === "undefined" || !window.location?.origin) return "";
  return window.location.origin;
}

function isLocalWindow() {
  return typeof window !== "undefined" && /^(localhost|127\.0\.0\.1)$/i.test(window.location?.hostname || "");
}

/**
 * Django core API origin, i.e. "<api origin>/api/core". LitigAI's rules:
 * a local dev origin is always same-origin; otherwise the configured API
 * base with "/api/core" appended exactly once ("/api" and "/api/core"
 * suffixes are not duplicated); with nothing configured, the current
 * origin, or the dev API when there is no window.
 */
export function resolveCoreApiBase() {
  const origin = windowOrigin();
  if (isLocalWindow() && origin) return `${origin}/api/core`;

  const configured = resolveApiBaseUrl() || (origin ? `${origin}/api/core` : DEFAULT_REMOTE_CORE_API_BASE);
  const trimmed = configured.replace(/\/+$/, "");
  if (trimmed.endsWith("/api/core")) return trimmed;
  if (trimmed.endsWith("/api")) return `${trimmed}/core`;
  return `${trimmed}/api/core`;
}

/**
 * Map an app-relative route to the path this build serves it at. SeriesAI
 * and in-house prefix dashboard routes with the appId; the others serve them
 * as written. Shared connector flows use this for OAuth return URLs.
 */
/**
 * The host's canonical form of an in-app path. The litigation dashboards rewrite
 * legacy and un-prefixed dashboard paths to their app-scoped form
 * (`canonicalPath` in the descriptor); a host without one gets the path back.
 * Distinct from resolveRoutePath, which prefixes a path with the current route's
 * workspace.
 */
export function resolveCanonicalPath(path) {
  const mapper = state.canonicalPath;
  if (typeof mapper === "function") {
    const mapped = mapper(path);
    if (typeof mapped === "string" && mapped) return mapped;
  }
  return path;
}

export function resolveRoutePath(path) {
  const mapper = state.routePath;
  if (typeof mapper === "function") {
    const mapped = mapper(path);
    if (typeof mapped === "string" && mapped) return mapped;
  }
  return path;
}

/**
 * Where an in-app link or redirect should point in this practice area: the
 * canonical form on hosts that declare one (litigation: /dashboard/<appId>/...),
 * otherwise the route-prefixed form (company workspaces), otherwise the path
 * as written. Shared screens use this instead of a per-app path helper.
 */
export function resolveAppPath(path) {
  if (typeof state.canonicalPath === "function") return resolveCanonicalPath(path);
  return resolveRoutePath(path);
}

/** The host's Redux store, unwrapping a thunk and an ESM/CJS module shape. Undefined when none is configured. */
export function getStore() {
  let store = unwrapThunk(state.store);
  if (store && typeof store === "object" && typeof store.getState !== "function" && store.default) {
    store = store.default;
  }
  return store && typeof store.getState === "function" ? store : undefined;
}

/**
 * The signed-in user's access token the way every fork's API modules read it:
 * Redux auth slice first, then the persisted localStorage copy, trimmed.
 */
export function getAuthAccessToken() {
  let token = "";
  try {
    token = getStore()?.getState?.()?.auth?.access || "";
  } catch (error) {
    token = "";
  }
  if (!token && typeof window !== "undefined") {
    try {
      token = window.localStorage?.getItem("access") || "";
    } catch (error) {
      token = "";
    }
  }
  return typeof token === "string" ? token.trim() : "";
}

function getToast() {
  let toast = unwrapThunk(state.toast);
  if (toast && typeof toast === "object" && typeof toast.error !== "function" && toast.default) {
    toast = toast.default;
  }
  return toast && typeof toast === "object" ? toast : undefined;
}

/** Surface an error to the user through the host's toast module; silent when none is configured. */
export function notifyError(message, ...rest) {
  const toast = getToast();
  if (typeof toast?.error === "function") toast.error(message, ...rest);
}

/** Surface a success message through the host's toast module; silent when none is configured. */
export function notifySuccess(message, ...rest) {
  const toast = getToast();
  if (typeof toast?.success === "function") toast.success(message, ...rest);
}

function getErrorReporter() {
  let reporter = unwrapThunk(state.sentry);
  if (reporter && typeof reporter === "object" && typeof reporter.captureMessage !== "function" && reporter.default) {
    reporter = reporter.default;
  }
  return reporter && typeof reporter === "object" ? reporter : undefined;
}

/** Report a message through the host's error reporter (`sentry` in the descriptor); silent when none is configured. */
export function captureHostMessage(...args) {
  const reporter = getErrorReporter();
  if (typeof reporter?.captureMessage === "function") return reporter.captureMessage(...args);
  return undefined;
}

/** True when the host's error reporter already captured this error (so it is not reported twice). */
export function hasHostCapturedError(error) {
  const reporter = getErrorReporter();
  return typeof reporter?.hasCapturedSentryError === "function" ? Boolean(reporter.hasCapturedSentryError(error)) : false;
}

/** Host override (SeriesAI, in-house and Atticus route this through their axiosInstance) or the shared rule. */
export function getCoreApiBase() {
  const override = trimTrailingSlash(unwrapThunk(state.coreApiBase));
  if (override) return override;
  // A host-supplied API base already encodes that app's localhost/origin
  // rules; only append the core path. Fall back to the shared rule otherwise.
  const configured = resolveApiBaseUrl();
  if (configured) {
    if (configured.endsWith("/api/core")) return configured;
    if (configured.endsWith("/api")) return `${configured}/core`;
    return `${configured}/api/core`;
  }
  return resolveCoreApiBase();
}
